import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import _ from 'lodash';
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { DateRangePicker, DateRangeDelimiter } from "@material-ui/pickers";
import {
  withStyles,
  makeStyles,
  createStyles,
} from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import * as Var from "styles/variables";
import {
  setDateRangePicTreeNtt,
  getHomePictreeCheckInNtt,
  getHomePictreePhotoShootNtt,
  getPictreeDataCheckInTrendNtt,
  getPictreeDataPhotoShootTrendNtt,
  getPictreeDataPhotoDenchuKaisuTrend,
  getInGameItemSalesByOccurrence,
  getPhotoShootMapDataNtt,
  getPictreePhotoShootByAssetNtt,
  getPictreePhotoShootTrendByAssetNtt,
  getHomePictreeReportStats,
  getPictreeDataReportTrend,
  getPictreeDataReportByUser,
  getByAreaData,
  getBudgetSummaryData,
  getBudgetRemainTrendData,
  getBudgetBoostData,
  getBudgetBoostSourceData,
} from "actions";
import PeriodNtt from "components/PicTree/Filter/PeriodNtt";

const useStyles = makeStyles((theme) =>
  createStyles({
    calendarWrapper: {
      paddingLeft: 8,
      flexGrow: 1,
    },
    root: {
      backgroundColor: "#fafafa",
      zIndex: 1000,
      paddingTop: 16,
      paddingLeft: 8,
      marginTop: -12,
    },
    container: {
      display: "flex",
      width: 300,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      alignItems: "baseline",
    },
    delimiter: {
      paddingTop: theme.spacing(1),
    },
    dataBtn: {
      marginTop: theme.spacing(1.2),
      marginLeft: theme.spacing(1),
      height: theme.spacing(4),
      color: Var.btnLabelColor,
    },
    grow: {
      flexGrow: 1,
    },
    sectionCalendar: {
      display: "flex",
      marginRight: theme.spacing(1),
      alignItems: "center",
      gap: theme.spacing(0.5),
    },
    picTreePredefined: {
      marginRight: 16,
      marginTop: -16,
    }
  })
);

const CalendarRangePicker = ({
  page,
  singlePeriod,
  dateRangePicTreeNtt,
  dateRangePicTreeMinMax,
  currentPage,
  getHomePictreeCheckInNtt,
  getHomePictreePhotoShootNtt,
  getPictreeDataCheckInTrendNtt,
  getPictreeDataPhotoShootTrendNtt,
  pictreeChartSize,
  getPhotoShootMapDataNtt,
  pictreeMapType,
  setDateRangePicTreeNtt,
  getPictreePhotoShootByAssetNtt,
  getPictreePhotoShootTrendByAssetNtt,
  getHomePictreeReportStats,
  getPictreeDataReportTrend,
  getPictreeDataReportByUser,
  getByAreaData,
  getBudgetSummaryData,
  getBudgetRemainTrendData,
  getBudgetBoostData,
  getBudgetBoostSourceData,
  selectedSeason,
}) => {
  const classes = useStyles();
  const [dateRangeValue, setDateRangeValue] = useState([null, null]);
  const [budgetSelectedSeason, setBudgetSelectedSeason] = useState(selectedSeason || []);
  const [budgetDateRange, setBudgetDateRange] = useState(dateRangePicTreeNtt || [null, null]);
  const [error, setError] = useState([null, null]);
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);

  const today = moment().tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
  const todayFormatted = moment(today).format("YYYY-MM-DD");

  useEffect(() => {
    const currentJstDate = moment().tz("Asia/Tokyo").format("YYYY/MM/DD");
    handleHomeCalendar(currentJstDate);
  }, []);

  useEffect(() => {
    if (Array.isArray(selectedSeason)) {
      setBudgetSelectedSeason(selectedSeason);
    }
  }, [selectedSeason]);

  useEffect(() => {
    if (Array.isArray(dateRangePicTreeNtt)) {
      setBudgetDateRange(dateRangePicTreeNtt);
    }
  }, [dateRangePicTreeNtt]);

  useEffect(() => {
    if (dateRangePicTreeMinMax.length > 0) {
      const defaultMaxDateFormatted = moment(dateRangePicTreeMinMax[1]).format('YYYY-MM-DD');
      const isEndDateAfterToday = moment(today).isBefore(defaultMaxDateFormatted);
      setMinDate(dateRangePicTreeMinMax[0]);
      setMaxDate(isEndDateAfterToday ? todayFormatted : dateRangePicTreeMinMax[1]);
    } else {
      setMinDate(null);
      setMaxDate(null);
    }
  }, [dateRangePicTreeMinMax]);

  useEffect(() => {
    const isEndDateAfterToday = moment(today).isBefore(dateRangePicTreeNtt[1]);
    const dateRangeValue = isEndDateAfterToday ? [dateRangePicTreeNtt[0], todayFormatted] : dateRangePicTreeNtt;
    setDateRangeValue(dateRangeValue);
  }, [dateRangePicTreeNtt]);

  const handleHomeCalendar = (currentJstDate) => {
    setDateRangeValue(dateRangePicTreeNtt);
  };

  const setRange = (newValue) => {
    const startDate = moment(newValue[0]).format("YYYY-MM-DD");
    const endDate = moment(newValue[1]).format("YYYY-MM-DD");
    setDateRangePicTreeNtt([startDate, endDate]);
    setDateRangeValue([startDate, endDate]);
    setBudgetDateRange([startDate, endDate]);
  };

  const handleBudgetSelectionChange = useCallback(({ selectedSeason: nextSelectedSeason, dateRange: nextDateRange }) => {
    if (Array.isArray(nextSelectedSeason)) {
      setBudgetSelectedSeason(nextSelectedSeason);
    }
    if (Array.isArray(nextDateRange)) {
      setBudgetDateRange(nextDateRange);
    }
  }, []);

  const fetchData = () => {
    const activePage = currentPage || window.location.pathname;

    if (activePage === "/game/pictree/ntt/checkin") {
      getHomePictreeCheckInNtt();
      getPictreeDataCheckInTrendNtt(pictreeChartSize);
    } else if (activePage === "/game/pictree/ntt/photos/overview") {
      getHomePictreePhotoShootNtt();
      getPictreeDataPhotoShootTrendNtt(pictreeChartSize);
    } else if (activePage === "/game/pictree/ntt/photos/map") {
      getPhotoShootMapDataNtt({ type: "denchu", filter: pictreeMapType });
    } else if (activePage === "/game/pictree/ntt/photos/asset-type") {
      getPictreePhotoShootByAssetNtt(pictreeChartSize);
      getPictreePhotoShootTrendByAssetNtt(pictreeChartSize);
    } else if (activePage === "/game/pictree/ntt/report/overview") {
      getHomePictreeReportStats("ntt");
      getPictreeDataReportTrend("ntt");
    } else if (activePage === "/game/pictree/ntt/report/report-by-user") {
      getPictreeDataReportByUser("ntt");
    } else if (activePage === "/game/pictree/ntt/photos/by-area") {
      getByAreaData({
        company: "ntt",
        mode: "summary",
      });
    } else if (activePage === "/game/pictree/ntt/budget") {
      const budgetPayload = {
        companyCode: "ntt",
        selectedSeason: budgetSelectedSeason,
        dateRange: budgetDateRange,
      };
      getBudgetSummaryData(budgetPayload);
      getBudgetRemainTrendData(budgetPayload);
      getBudgetBoostSourceData(budgetPayload);
      getBudgetBoostData(budgetPayload);
    }
  };

  return (
    <div className={classes.calendarWrapper}>
      <div className={classes.root}>
        <div className={classes.sectionCalendar}>
          <div className={classes.picTreePredefined}>
            <PeriodNtt
              singlePeriod
              currentPage={currentPage}
              onBudgetSelectionChange={handleBudgetSelectionChange}
            />
          </div>
          <DateRangePicker
            startText="Start"
            endText="End"
            disableHighlightToday={true}
            showToolbar={false}
            allowSameDateSelection={true}
            minDate={minDate}
            maxDate={maxDate}
            value={dateRangeValue}
            onChange={(newValue) => setRange(newValue)}
            onError={([startReason, endReason], [start, end]) => {
              if (startReason === "invalidRange") {
                setError([null, null]);
                return;
              }
              setError([startReason, endReason]);
            }}
            renderInput={(startProps, endProps) => (
              <div className={classes.container}>
                <TextField
                  id="standard-basic"
                  {...startProps}
                  error={Boolean(error[0])}
                  size="small"
                  disabled={page === "pictree-map" ? true : false}
                  helperText=""
                />
                <DateRangeDelimiter>
                  <span className={classes.delimiter}>~</span>
                </DateRangeDelimiter>
                <TextField
                  id="standard-basic"
                  {...endProps}
                  error={Boolean(error[0])}
                  size="small"
                  disabled={page === "pictree-map" ? true : false}
                  helperText=""
                />
              </div>
            )}
          />
          <Button
            className={classes.dataBtn}
            variant="contained"
            color="primary"
            onClick={() => fetchData()}
          >
            GO
          </Button>
        </div>
      </div>
    </div>
  );
};

CalendarRangePicker.prototypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  btnStateDataFetch: state.page.btnStateDataFetch,
  dateRangePicTreeNtt: state.page.dateRangePicTreeNtt,
  dateRangePicTreeMinMax: state.page.dateRangePicTreeMinMax,
  currentPage: state.page.currentPage,
  pictreeView: state.pictree.pictreeView,
  pictreeChartSize: state.pictree.chartSize,
  pictreeMapType: state.pictree.mapType,
  selectedSeason: state.pictreeNtt.selectedSeason,
});

const mapDispatchToProps = (dispatch) => ({
  setDateRangePicTreeNtt: (param) => dispatch(setDateRangePicTreeNtt(param)),
  getHomePictreeCheckInNtt: (param) => dispatch(getHomePictreeCheckInNtt(param)),
  getHomePictreePhotoShootNtt: (param) => dispatch(getHomePictreePhotoShootNtt(param)),
  getPictreeDataCheckInTrendNtt: (param) => dispatch(getPictreeDataCheckInTrendNtt(param)),
  getPictreeDataPhotoShootTrendNtt: (param) => dispatch(getPictreeDataPhotoShootTrendNtt(param)),
  getPictreeDataPhotoDenchuKaisuTrend: (param) => dispatch(getPictreeDataPhotoDenchuKaisuTrend(param)),
  getInGameItemSalesByOccurrence: (param) => dispatch(getInGameItemSalesByOccurrence(param)),
  getPhotoShootMapDataNtt: (param) => dispatch(getPhotoShootMapDataNtt(param)),
  getPictreePhotoShootByAssetNtt: (param) => dispatch(getPictreePhotoShootByAssetNtt(param)),
  getPictreePhotoShootTrendByAssetNtt: (param) => dispatch(getPictreePhotoShootTrendByAssetNtt(param)),
  getHomePictreeReportStats: (param) => dispatch(getHomePictreeReportStats(param)),
  getPictreeDataReportTrend: (param) => dispatch(getPictreeDataReportTrend(param)),
  getPictreeDataReportByUser: (param) => dispatch(getPictreeDataReportByUser(param)),
  getByAreaData: (param) => dispatch(getByAreaData(param)),
  getBudgetSummaryData: (param) => dispatch(getBudgetSummaryData(param)),
  getBudgetRemainTrendData: (param) => dispatch(getBudgetRemainTrendData(param)),
  getBudgetBoostData: (param) => dispatch(getBudgetBoostData(param)),
  getBudgetBoostSourceData: (param) => dispatch(getBudgetBoostSourceData(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(CalendarRangePicker));
